import { getApiBaseUrl } from './chatIdentity';

// Driver profile picture. The backend re-encodes uploads to a square JPEG and
// returns a short-lived presigned URL; /auth/me returns the same URL on load.
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';

const AVATAR_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const AVATAR_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif)$/i;
const HEIC_EXTENSIONS = /\.(heic|heif)$/i;

// Client-side check for fast feedback only; the server re-validates the bytes.
export function getAvatarFileError(file) {
    if (!file) return 'Choose an image to upload.';
    const type = (file.type || '').toLowerCase();
    // Some browsers (e.g. Chrome on Windows) report HEIC files with an empty or generic
    // type, so fall back to the extension whenever the type is not a known image type.
    const supported = AVATAR_MIME_TYPES.has(type) || AVATAR_EXTENSIONS.test(file.name || '');
    if (!supported) return 'Upload a JPG, PNG, WebP, or HEIC image.';
    if (file.size > AVATAR_MAX_BYTES) return 'Profile pictures must be 5 MB or smaller.';
    if (file.size === 0) return 'That file is empty. Choose another image.';
    return '';
}

export function isHeicFile(file) {
    const type = (file?.type || '').toLowerCase();
    return type === 'image/heic' || type === 'image/heif' || HEIC_EXTENSIONS.test(file?.name || '');
}

async function readAvatarResponse(response, fallbackMessage, tooLargeMessage = 'Profile pictures must be 5 MB or smaller.') {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        if (response.status === 401) throw new Error('Your session expired. Sign in again to update your photo.');
        if (response.status === 413) throw new Error(tooLargeMessage);
        throw new Error(payload.detail || payload.message || payload.error || fallbackMessage);
    }
    return payload.avatar_url || '';
}

export async function uploadAvatar(file) {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${getApiBaseUrl()}/api/account/avatar`, {
        method: 'POST',
        credentials: 'include',
        body,
    });
    return readAvatarResponse(response, 'Unable to upload your profile picture.');
}

// Shop portal: the shop's photo. Same rules and processing as the driver picture; the
// shop comes from the shop session cookie.
const SHOP_PHOTO_TOO_LARGE = 'Shop photos must be 5 MB or smaller.';

export async function uploadShopPhoto(file) {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${getApiBaseUrl()}/api/shop/photo`, {
        method: 'POST',
        credentials: 'include',
        body,
    });
    return readAvatarResponse(response, 'Unable to upload the shop photo.', SHOP_PHOTO_TOO_LARGE);
}

export async function removeShopPhoto() {
    const response = await fetch(`${getApiBaseUrl()}/api/shop/photo`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return readAvatarResponse(response, 'Unable to remove the shop photo.', SHOP_PHOTO_TOO_LARGE);
}

export async function removeAvatar() {
    const response = await fetch(`${getApiBaseUrl()}/api/account/avatar`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return readAvatarResponse(response, 'Unable to remove your profile picture.');
}

// Garage vehicle photo. Same rules/processing as the driver picture; the backend
// re-encodes to a square JPEG, stores it on the vehicle, and returns a presigned URL
// (also returned per-vehicle by /api/lookup_user_data). Separate from Glove Box docs.
const VEHICLE_PHOTO_TOO_LARGE = 'Vehicle photos must be 5 MB or smaller.';

async function readVehiclePhotoResponse(response, fallbackMessage) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        if (response.status === 401) throw new Error('Your session expired. Sign in again to update the photo.');
        if (response.status === 413) throw new Error(VEHICLE_PHOTO_TOO_LARGE);
        throw new Error(payload.detail || payload.message || payload.error || fallbackMessage);
    }
    return payload.photo_url || '';
}

export async function uploadVehiclePhoto(vehicleId, file) {
    const body = new FormData();
    body.append('file', file);
    const response = await fetch(`${getApiBaseUrl()}/api/vehicles/${encodeURIComponent(vehicleId)}/photo`, {
        method: 'POST',
        credentials: 'include',
        body,
    });
    return readVehiclePhotoResponse(response, 'Unable to upload the vehicle photo.');
}

export async function removeVehiclePhoto(vehicleId) {
    const response = await fetch(`${getApiBaseUrl()}/api/vehicles/${encodeURIComponent(vehicleId)}/photo`, {
        method: 'DELETE',
        credentials: 'include',
    });
    return readVehiclePhotoResponse(response, 'Unable to remove the vehicle photo.');
}
